"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { X } from "lucide-react";

import { useStyles } from "./styles";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { createRoom, setShowSignupModal } from "@/redux/actions";
import FacebookButton from "@/components/auth/FacebookButton";
import GoogleButton from "@/components/auth/GoogleButton";

const SignupModal = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [isLoginMode, setIsLoginMode] = useState(false);
  const [subscribeToEmails, setSubscribeToEmails] = useState(true);
  const [mounted, setMounted] = useState(false);

  const isEmailAuth = useRef(false);

  const open = useAppSelector((state) => state.user.showSignupModal);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClose = () => {
    dispatch(setShowSignupModal(false));
    setIsLoginMode(false);
    isEmailAuth.current = false;
  };

  const handleSuccessfulAuth = async (
    authType: "email" | "oauth" = "email"
  ) => {
    if (authType === "oauth") {
      dispatch(setShowSignupModal(false));
      return;
    }

    isEmailAuth.current = true;

    localStorage.setItem("emailAuthGeneration", "true");

    const draft = localStorage.getItem("generationDraft");
    if (draft) {
      try {
        const {
          prompt,
          images,
          imageMask,
          landingPageSlug,
          defaultAspectRatio,
        } = JSON.parse(draft);

        dispatch(setShowSignupModal(false));

        const ideaUUID = await dispatch(
          createRoom({
            prompt,
            inspiration: images || [],
            landingPageSlug,
            defaultAspectRatio,
            imageMask,
          })
        ).unwrap();

        localStorage.removeItem("generationDraft");
        localStorage.removeItem("emailAuthGeneration");

        if (ideaUUID) {
          router.push(`/idea/${ideaUUID}`);
        }
      } catch (error) {
        console.error("Failed to handle email auth success:", error);
        localStorage.removeItem("emailAuthGeneration");
        dispatch(setShowSignupModal(false));
        router.push("/");
      }
    } else {
      localStorage.removeItem("emailAuthGeneration");
      dispatch(setShowSignupModal(false));
      router.push("/");
    }
  };

  if (!mounted || !open) return null;

  const modalContent = (
    <div className={classes.modalWrapper}>
      <div className={classes.backdrop} onClick={handleClose} />
      <div className={classes.modalContainer}>
        <div className={classes.modal}>
          <button
            onClick={handleClose}
            className={classes.closeButton}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <Typography variant="h5" className={classes.heading}>
            {isLoginMode ? "Sign in to continue" : "Sign up to continue"}
          </Typography>

          <Typography variant="body2" className={classes.subheading}>
            Create an account to generate your AI images
          </Typography>

          <div className={classes.formContent}>
            {!isLoginMode && (
              <FormGroup className={classes.checkboxGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={subscribeToEmails}
                      onChange={(e) => setSubscribeToEmails((prev) => !prev)}
                      className={classes.checkbox}
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      className={classes.checkboxLabel}
                    >
                      Subscribe to email notifications
                    </Typography>
                  }
                />
              </FormGroup>
            )}

            <div className={classes.termsText}>
              <Typography variant="caption">
                By signing up you are agreeing to our{" "}
                <a
                  href="https://idyllic.app/terms-conditions/"
                  target="_blank"
                  rel="noreferrer"
                  className={classes.link}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://idyllic.app/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                  className={classes.link}
                >
                  Privacy Policy
                </a>
                .
              </Typography>
            </div>

            <div className={classes.authButtons}>
              <GoogleButton
                actionType={isLoginMode ? "login" : "register"}
                subscribeToEmails={subscribeToEmails}
                className={classes.googleButton}
                onSuccess={() => handleSuccessfulAuth("oauth")}
              />
              <FacebookButton
                actionType={isLoginMode ? "login" : "register"}
                subscribeToEmails={subscribeToEmails}
                className={classes.facebookButton}
                onSuccess={() => handleSuccessfulAuth("oauth")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SignupModal;
